import { useState, useCallback, useRef, useEffect } from "react";
import { streamChatTurn, dispatchDocumentOperation, fetchSessionContext } from "../utils/conversationApi";
import normalizeResumeData from "../utils/normalizeResumeData";
import { migrateResume } from "../core/schema/migrateResume";

export function useConversationSession({ authToken, triggerLightningAnimation, onCoverLetterUpdate, onBackendMutation, onCreditsUpdate }) {
  const [conversation, setConversation] = useState([]);
  const [activeResume, _setActiveResume] = useState(null);
  const setActiveResume = useCallback((val) => {
    if (typeof val === 'function') {
      _setActiveResume(prev => {
        const res = val(prev);
        return res ? normalizeResumeData(migrateResume(res)) : null;
      });
    } else {
      _setActiveResume(val ? normalizeResumeData(migrateResume(val)) : null);
    }
  }, []);
  const [atsScore, setAtsScore] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);

  // ── Phase 3: Intelligence Core state ──────────────────────────────
  const [conversationMode, setConversationMode] = useState('ask');      // active conversation mode
  const [completeness, setCompleteness] = useState(null);               // CareerCompletenessIndex dict
  const [opportunities, setOpportunities] = useState([]);               // ConversationPriority[]
  const [mutationFeedback, setMutationFeedback] = useState([]);         // [{label, delta}] shown as toast
  const atsPrevRef = useRef(null);                                       // prev ATS score for delta
  const [pipelineEvents, setPipelineEvents] = useState([]);
  const [latestDebugPayload, setLatestDebugPayload] = useState(null);

  // Entry intent: set by Dashboard cards and CreateMenu before first turn
  const setEntryIntent = useCallback((intent) => {
    if (intent) {
      sessionStorage.setItem("mmr_pending_entry_intent", intent);
    } else {
      sessionStorage.removeItem("mmr_pending_entry_intent");
    }
    setSession(prev => prev ? { ...prev, entry_intent: intent } : { entry_intent: intent });
  }, []);

  // Template state — canonical kebab-case IDs only (no aliases stored here)
  const [template, setTemplate] = useState("startup-neon");
  const [templateVersion, setTemplateVersion] = useState("2.2.0");
  const [accentColor, setAccentColor] = useState("#000000");
  const [fontScale, setFontScale] = useState(1);
  const [fontFamily, setFontFamily] = useState("Inter");
  const [coverTemplate, setCoverTemplate] = useState("modern_ats_pro");

  const abortRef = useRef(null);

  // Sync style state from the canonical backend source fields on activeResume.
  // S3 of the template pipeline — reads content._template after document load/reload.
  useEffect(() => {
    if (activeResume) {
      const themeTemplate = activeResume._template;
      const themeVersion  = activeResume._style_state?.theme?.template_version;
      if (themeTemplate) {
        const normalized = themeTemplate.replace(/_/g, '-').toLowerCase();
        if (process.env.NODE_ENV === 'development') {
          console.log(`[PIPE:template S3:session-sync] activeResume._template=${themeTemplate} → ${normalized}`);
        }
        setTemplate(normalized);
      }
      if (themeVersion)  setTemplateVersion(themeVersion);
      if (activeResume._accentColor) setAccentColor(activeResume._accentColor);
      if (activeResume._fontFamily)  setFontFamily(activeResume._fontFamily);
      if (activeResume._fontScale)   setFontScale(parseFloat(activeResume._fontScale || '1'));
    }
  }, [activeResume]);


  // Persist session metadata for browser reload restore
  useEffect(() => {
    if (session) {
      localStorage.setItem("rz_active_doc_type", session.type || "master");
      localStorage.setItem("rz_active_doc_id", session.document_id || "master");
    }
  }, [session]);

  const loadConversation = useCallback(async (docType, docId, isMaster) => {
    if (!authToken) return;
    setLoading(true);
    setError(null);
    setIsStreaming(false);
    setStreamingText('');
    setChatStatus('IDLE');
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (e) {}
    }
    try {
      const pendingIntent = sessionStorage.getItem("mmr_pending_entry_intent");
      sessionStorage.removeItem("mmr_pending_entry_intent");

      const data = await fetchSessionContext({
        authToken,
        docType,
        docId,
        isMaster,
        entryIntent: pendingIntent || null,
      });
      setSession({
        session_id: data.id,
        conversation_id: data.id,
        document_id: docId,
        workspace_id: data.workspace_id || "",
        type: docType,
        entry_intent: pendingIntent || null,
      });
      setConversation(data.messages || []);

      // Fetch verified state from the backend
      const docRes = await fetch(`/api/document?type=${docType}&id=${docId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (docRes.ok) {
        const docData = await docRes.json();

        // For cover letter sessions: update the cover letter preview panel
        if (docType === 'cover_letter') {
          const clState = docData.state?.content;
          if (clState && onCoverLetterUpdate) {
            // state.content = { content, company, job_title }
            onCoverLetterUpdate({
              id: docId,
              content: clState.content || '',
              company: clState.company || '',
              job_title: clState.job_title || '',
              recipient: clState.recipient || '',
              sender: clState.sender || {},
              template: docData.state?.style?.theme?.template || 'modern_ats_pro',
              created_at: docData.document?.created_at,
            });
          }
        } else {
          const resumeContent = docData.state?.content || docData.document_data || docData;
          setActiveResume(normalizeResumeData(resumeContent));
          // Bug 5 fix: populate ATS score from initial document load so the ATS tab
          // is not blank before the first chat DONE event.
          if (docData.ats_score || docData.ats) {
            setAtsScore(docData.ats_score || docData.ats);
          }
        }
      }

    } catch (err) {
      setError(err.message || "Failed to load session");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // ── Chat status state machine ──────────────────────────────────────
  // IDLE → READING_RESUME → ANALYZING → GENERATING → UPDATING_RESUME → SCORING_ATS → DONE
  const [chatStatus, setChatStatus] = useState('IDLE');

  const sendMessage = useCallback(async (text) => {
    const message = typeof text === 'string' ? text : '';
    if (!authToken || isStreaming || !message.trim()) return;

    const t0 = Date.now();
    const traceId = Math.random().toString(36).slice(2, 8);
    console.log(`[TRACE:hook] ── sendMessage() START [${traceId}] ── ${new Date().toISOString()}`);
    console.log(`[TRACE:hook] [${traceId}] message length=${message.length} | session=${JSON.stringify(session)} | isStreaming=${isStreaming}`);

    setIsStreaming(true);
    setStreamingText('');
    setPipelineEvents([]);
    setError(null);
    setChatStatus('READING_RESUME');
    console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — state: isStreaming=true, chatStatus=READING_RESUME`);

    const userMsg = { role: "user", content: message, timestamp: new Date().toISOString() };
    const updatedReply = [...conversation, userMsg];
    setConversation(updatedReply);

    // Flag the upcoming AI reply index as already typed/streamed to prevent double-typing in the history list
    sessionStorage.setItem(`welcome_typed_${conversation.length + 1}`, 'true');

    const workspaceCtx = {
      session_id: session?.session_id,
      document_id: session?.document_id,
      document_type: session?.type,         // Document-First: tells backend which schema to use
      workspace_id: session?.workspace_id,
      entry_intent: session?.entry_intent || null,  // Entry Point Routing: set by Dashboard/CreateMenu
      message_timestamp: userMsg.timestamp,          // Temporal context for AI (prevents time hallucinations)
    };

    if (abortRef.current) abortRef.current.abort();

    try {
      console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — streamChatTurn() starting`);
      console.log(`[TRACE:hook] [${traceId}]   sessionId=${session?.session_id} | docId=${session?.document_id} | workspaceId=${session?.workspace_id}`);

      const controller = await streamChatTurn({
        authToken,
        reply: updatedReply,
        sessionId: session?.session_id,
        workspaceCtx,
        onThinking: (msg) => {
          const lower = (msg || '').toLowerCase();
          if (lower.includes('read') || lower.includes('resume')) {
            setChatStatus('READING_RESUME');
          } else if (lower.includes('analyz')) {
            setChatStatus('ANALYZING');
          } else if (lower.includes('generat') || lower.includes('writ')) {
            setChatStatus('GENERATING');
          } else if (lower.includes('updat') || lower.includes('apply')) {
            setChatStatus('UPDATING_RESUME');
          } else if (lower.includes('scor') || lower.includes('ats')) {
            setChatStatus('SCORING_ATS');
          } else {
            setChatStatus('ANALYZING');
          }
          setStreamingText(msg);
          console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — THINKING: "${msg}" | chatStatus updated`);
        },
        onChunk: (chunk, acc) => {
          setChatStatus('GENERATING');
          setStreamingText(acc);
        },
        onReplace: (text) => {
          setChatStatus('GENERATING');
          setStreamingText(text);
        },
        onDone: (donePayload) => {
          console.log("[TRACE:DONE]", {
            id: donePayload.cover_letter?.id,
            company: donePayload.cover_letter?.company,
            job_title: donePayload.cover_letter?.job_title,
            recipient: donePayload.cover_letter?.recipient,
            created_at: donePayload.cover_letter?.created_at,
            contentLength: donePayload.cover_letter?.content?.length || 0,
            keys: donePayload.cover_letter ? Object.keys(donePayload.cover_letter) : []
          });
          const tDone = Date.now();
          console.log(`[TRACE:hook] [${traceId}] ${tDone-t0}ms — ── onDone() ENTERED ──`);
          console.log(`[TRACE:hook] [${traceId}]   payload keys: [${Object.keys(donePayload).join(', ')}]`);
          console.log(`[TRACE:hook] [${traceId}]   .resume       = ${donePayload.resume ? 'object' : 'MISSING'}`);
          console.log(`[TRACE:hook] [${traceId}]   .resume_data  = ${donePayload.resume_data ? 'object' : 'MISSING'}`);
          console.log(`[TRACE:hook] [${traceId}]   .conversation = ${typeof donePayload.conversation} ${donePayload.conversation?.messages ? `(messages: ${donePayload.conversation.messages.length})` : ''}`);
          console.log(`[TRACE:hook] [${traceId}]   .ats          = ${donePayload.ats ? JSON.stringify(donePayload.ats).slice(0,80) : 'MISSING'}`);
          setChatStatus('UPDATING_RESUME');

          // Unified payload consumption in a single render tick:
          const rawResume = donePayload.resume_data || donePayload.resume;
          // ── [TRACE:BugB] — What did the DONE payload contain? ──
          console.log(`[TRACE:BugB] DONE payload resume → name=${rawResume?.name || 'MISSING'} | role=${rawResume?.role || 'MISSING'} | doc_id=${donePayload.session?.document_id || 'n/a'}`);
          const finalResume = normalizeResumeData(rawResume);
          console.log(`[TRACE:BugB] after normalize → name=${finalResume?.name || 'MISSING'} | finalResume=${finalResume ? 'OK' : 'NULL'}`);
          console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — setActiveResume() called | resumeValid=${!!finalResume} | name=${finalResume?.name || 'n/a'}`);
          setActiveResume(finalResume);

          if (donePayload.metadata && onBackendMutation) {
            onBackendMutation(donePayload.metadata);
          }


          const msgs =
            donePayload.session_messages ||
            donePayload.conversation?.messages ||
            (Array.isArray(donePayload.conversation) ? donePayload.conversation : []);
          console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — setConversation() called | messages=${msgs.length}`);
          setConversation(msgs);

          if (donePayload.ats) {
            setChatStatus('SCORING_ATS');
            console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — setAtsScore() called | score=${donePayload.ats?.overall_score}`);
            setAtsScore(donePayload.ats);
          } else {
            console.warn(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — ATS MISSING from DONE payload`);
          }

          // Update cover letter preview without a re-fetch
          if (donePayload.cover_letter) {
            const cl = donePayload.cover_letter;
            console.log("[TRACE:CL_META] DONE payload cover_letter received:", JSON.stringify(cl));
            // [TRACE:CL6]
            console.log(`[TRACE:CL6] DONE payload received, cover_letter content length: ${(cl.content || '').length}`);
            console.log(`[TRACE:cl] DONE payload cover_letter = id=${cl.id} company=${cl.company} job_title=${cl.job_title} hasContent=${!!cl.content} content_len=${(cl.content||'').length}`);
            if (onCoverLetterUpdate) {
              onCoverLetterUpdate(cl);
              console.log(`[TRACE:cl] onCoverLetterUpdate() called OK`);
            } else {
              console.warn(`[TRACE:cl] onCoverLetterUpdate is undefined -- activeCoverLetter will NOT update`);
            }
          } else {
            console.log(`[TRACE:cl] DONE payload has no cover_letter field (resume session or CL not updated)`);
          }


          console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — setStreamingText('') | setIsStreaming(false) | setChatStatus(DONE)`);
          setStreamingText('');
          setIsStreaming(false);
          setChatStatus('DONE');

          console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — ── UI UNLOCKED ── isStreaming=false`);
          setTimeout(() => setChatStatus('IDLE'), 1500);

          // ── Phase 3: Intelligence Core outputs ────────────────────
          if (donePayload.conversation_mode) {
            setConversationMode(donePayload.conversation_mode);
          }
          if (donePayload.completeness) {
            setCompleteness(donePayload.completeness);
          }
          if (donePayload.opportunities) {
            setOpportunities(donePayload.opportunities);
          }

          // ── Mutation feedback toast & inline chat confirmations ────
          const updatedSections = donePayload.status?.updated_sections || [];
          if (updatedSections.length > 0) {
            const newAts = donePayload.ats?.overall_score || 0;
            const oldAts = atsPrevRef.current || 0;
            const atsDelta = newAts - oldAts;
            const feedbackItems = updatedSections.map((s) => ({
              label: `${s.charAt(0).toUpperCase() + s.slice(1)} updated`,
              atsDelta: s === updatedSections[0] && atsDelta > 0 ? atsDelta : null,
            }));
            setMutationFeedback(feedbackItems);
            // Auto-clear after 3.5 seconds
            setTimeout(() => setMutationFeedback([]), 3500);

            // Emit inline system confirmation messages
            if (donePayload.resume) {
              const resume = donePayload.resume;
              const newSystemMsgs = [];
              
              updatedSections.forEach(section => {
                let text = "";
                if (section === "experience" && resume.experience && resume.experience.length > 0) {
                  const latest = resume.experience[0];
                  const company = latest.company || "Unknown Company";
                  const role = latest.role || latest.position || "Developer";
                  text = `✅ Experience saved\n    ${company} | ${role}`;
                } else if (section === "skills" && resume.skills && resume.skills.length > 0) {
                  const skillsList = resume.skills.map(s => typeof s === 'string' ? s : s.name || s.value).filter(Boolean);
                  text = `✅ Skills saved\n    ${skillsList.join(", ")}`;
                } else if (section === "education" && resume.education && resume.education.length > 0) {
                  const latest = resume.education[0];
                  const college = latest.college || latest.institution || "University";
                  const degree = latest.degree || "Degree";
                  text = `✅ Education saved\n    ${degree} | ${college}`;
                } else if (section === "summary" && resume.summary) {
                  text = `✅ Summary saved\n    ${resume.summary}`;
                } else if (section === "projects" && resume.projects && resume.projects.length > 0) {
                  const latest = resume.projects[0];
                  const name = latest.name || "Project";
                  text = `✅ Projects saved\n    ${name}`;
                } else if (section === "contact" || section === "identity") {
                  const name = resume.name || "";
                  const email = resume.email || "";
                  const phone = resume.phone || "";
                  text = `✅ Contact saved\n    ${name} | ${email} | ${phone}`;
                }
                
                if (text) {
                  newSystemMsgs.push({
                    role: "system_confirmation",
                    content: text,
                    timestamp: Date.now()
                  });
                }
              });

              if (newSystemMsgs.length > 0) {
                setConversation(prev => [...prev, ...newSystemMsgs]);
                setTimeout(() => {
                  setConversation(prev => prev.filter(m => m.role !== "system_confirmation"));
                }, 3000);
              }
            }
          }
          atsPrevRef.current = donePayload.ats?.overall_score || atsPrevRef.current;

          if (donePayload.debug) {
            // Merge conversation_brief into the debug payload so AIDebugPanel can read it
            const debugWithBrief = donePayload.conversation_brief
              ? { ...donePayload.debug, conversation_brief: donePayload.conversation_brief }
              : donePayload.debug;
            setLatestDebugPayload(debugWithBrief);
          }

          // Fetch fresh credit balance from account endpoint
          if (onCreditsUpdate) {
            fetch("/api/payments/credits", {
              headers: { Authorization: `Bearer ${authToken}` }
            })
            .then(res => {
              if (res.ok) return res.json();
              throw new Error("Failed to fetch credits");
            })
            .then(data => {
              if (typeof data.credits === 'number') {
                onCreditsUpdate(data.credits);
              }
            })
            .catch(err => console.warn("[credits-sync] refresh failed:", err));
          }

          if (triggerLightningAnimation) triggerLightningAnimation();
        },
        onError: (err) => {
          console.error(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — STREAM ERROR: ${err}`);
          console.error(`[TRACE:hook] [${traceId}]   Setting isStreaming=false, chatStatus=IDLE`);
          setError(err);
          setIsStreaming(false);
          setChatStatus('IDLE');
        },
        onPipelineEvent: (ev) => {
          setPipelineEvents(prev => {
            const exists = prev.some(e => e.stage === ev.stage);
            if (exists) {
              return prev.map(e => e.stage === ev.stage ? { ...e, ...ev } : e);
            }
            return [...prev, ev];
          });
        },
        onPreviewUpdate: async (ev) => {
          // Preview Live Sync: immediately re-fetch the document to update the preview canvas
          console.log(`[preview_update] section=${ev.section || 'unknown'} — re-fetching document`);
          if (!session) return;
          try {
            const docId = session.document_id || 'master';
            const docType = session.type || 'resume';
            const docRes = await fetch(`/api/document?type=${docType}&id=${docId}`, {
              headers: { Authorization: `Bearer ${authToken}` },
            });
            if (docRes.ok) {
              const docData = await docRes.json();
              const resumeContent = docData.state?.content || docData.document_data || docData;
              setActiveResume(normalizeResumeData(resumeContent));
              console.log(`[preview_update] preview refreshed for doc=${docId}`);
            }
          } catch (err) {
            console.warn(`[preview_update] re-fetch failed:`, err.message);
          }
        },
      });
      abortRef.current = controller;
      console.log(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — streamChatTurn() resolved, abortRef assigned`);
    } catch (err) {
      console.error(`[TRACE:hook] [${traceId}] ${Date.now()-t0}ms — CATCH: ${err.message}`);
      console.error(`[TRACE:hook] [${traceId}]   Setting isStreaming=false, chatStatus=IDLE`);
      setError(err.message);
      setIsStreaming(false);
      setChatStatus('IDLE');
    }
  }, [authToken, conversation, session, isStreaming, triggerLightningAnimation, onBackendMutation]);

  const dispatchOperation = useCallback(async (operation) => {
    if (!authToken) return;
    setError(null);
    try {
      const activeDocument = {
        type: session?.type || "master_resume",
        id: session?.document_id || "master",
      };
      const res = await dispatchDocumentOperation({
        authToken,
        operation,
        activeDocument,
      });
      if (res.success) {
        // Single render tick update
        setActiveResume(normalizeResumeData(res.resume_data || res.resume));
        if (res.ats) setAtsScore(res.ats);
      } else {
        setError("I couldn't verify that information.");
      }
    } catch (err) {
      setError("I couldn't verify that information.");
    }
  }, [authToken, session]);

  const handleStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      setIsStreaming(false);
      setStreamingText("");
    }
  }, []);

  return {
    conversation,
    setConversation,
    activeResume,
    setActiveResume,
    atsScore,
    setAtsScore,
    isStreaming,
    setIsStreaming,
    streamingText,
    setStreamingText,
    chatStatus,
    loading,
    session,
    setSession,
    error,
    setError,
    loadConversation,
    sendMessage,
    dispatchOperation,
    handleStop,
    setEntryIntent,   // Set entry_intent on session for Turn 1 routing
    template,
    setTemplate,
    templateVersion,
    setTemplateVersion,
    accentColor,
    setAccentColor,
    fontScale,
    setFontScale,
    fontFamily,
    setFontFamily,
    coverTemplate,
    setCoverTemplate,
    // Phase 3 Intelligence Core
    conversationMode,
    completeness,
    opportunities,
    mutationFeedback,
    pipelineEvents,
    setPipelineEvents,
    latestDebugPayload,
    setLatestDebugPayload,
  };
}
